import React, { useEffect, useRef, useState } from 'react';
import Editor, { DiffEditor, useMonaco, loader } from '@monaco-editor/react';
import { Button, Typography } from '@primitives';
import { keywords, languageConfig, theme } from '@cp/pages/Paramount/EditStrategy/constants';
import Split from 'react-split';
import ViewStrategy from '@cp/pages/Paramount/ViewStrategy';
import './index.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { creation, getTemplate, validate } from '../paramountActions';
import useGetTenant from '@cp/pages/Paramount/hooks/useGetTenant';
import Documentation from '@cp/pages/Paramount/Documentation';
import Trigger from '@cp/pages/Paramount/Documentation/Trigger';
import AttributesModal from '@cp/pages/Paramount/Timelines/ViewTimelines/AttributesModal';
import { noop } from '@utils/common';
import APP_ROUTES from '@cp/src/layout/Routes';
import ActionsModal from '@cp/pages/Paramount/EditStrategy/ActionsModal';

function EditStrategy() {
  const editorRef = useRef(null);
  const monaco = useMonaco();
  const dispatch = useDispatch();
  const tenant = useGetTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const entityType = searchParams.get('entity') || '';
  const creationType = searchParams.get('type') || '';
  const [template, setTemplate] = useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);

  const templateEntityMap = {
    strategy: 'STRATEGY',
    trigger: 'TRIGGER',
    segment: 'SEGMENT'
  };

  const fetchTemplate = () => {
    getTemplate(tenant, templateEntityMap[entityType]).then(res => {
      setTemplate(res.data);
    });
  };

  useEffect(() => {
    fetchTemplate();
  }, [entityType]);

  const handleValidateStrategy = () => {
    const value = editorRef.current?.getValue();
    dispatch(
      validate({
        data: value,
        type: entityType.toUpperCase(),
        tenant: tenant
      })
    );
  };

  const handleSubmitStrategy = () => {
    const value = editorRef.current?.getValue();
    dispatch(
      creation({
        data: value,
        type: entityType.toUpperCase(),
        tenant: tenant
      })
    );
  };

  useEffect(() => {
    if (monaco) {
      monaco?.languages?.register({ id: 'strategyLanguage' });
      monaco?.languages?.setMonarchTokensProvider('strategyLanguage', {
        ...languageConfig
      });
      monaco.editor.defineTheme('strategy-theme', {
        ...theme
      });

      monaco?.languages?.registerCompletionItemProvider('strategyLanguage', {
        provideCompletionItems: function (model, position) {
          const suggestions = [
            ...keywords.map(k => {
              return {
                label: k,
                kind: monaco.languages.CompletionItemKind.Keyword,
                insertText: k
              };
            })
          ];
          return {
            suggestions: suggestions
          };
        }
      });
    }
  }, [monaco]);

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;
  }

  const navigate = useNavigate();

  const handleViewAttribute = () => {
    setIsOpen(true);
  };

  const handleViewActions = () => {
    setIsActionsModalOpen(true);
  };

  return (
    <div>
      <div className="topNavContainer flex justify-between">
        <div className={cx('cursor-pointer')} onClick={() => navigate(-1)}>
          <BackIcon size={'16'} />
        </div>
        <div className="">
          <Button variant="primary" onClick={handleViewAttribute}>
            View Attributes
          </Button>
          <Button
            variant="primary"
            className="ml-4"
            onClick={() => {
              navigate(`${APP_ROUTES.PARAMOUNT_SEGMENTS.path}`);
            }}
          >
            View Segments
          </Button>
          <Button variant="primary" className="ml-4" onClick={handleViewActions}>
            View Actions
          </Button>
        </div>
      </div>
      <div className="px-4">
        <Typography variant="h1" className="ml-0 mt-4 mb-4">
          {' '}
          Create {entityType}
        </Typography>
        <Split
          className="split"
          sizes={[50, 50]}
          minSize={100}
          expandToMin={false}
          gutterSize={20}
          gutterAlign="center"
          snapOffset={30}
          dragInterval={1}
          direction="horizontal"
          cursor="col-resize"
        >
          <div>
            <Editor
              height="75vh"
              defaultLanguage="strategyLanguage"
              defaultValue="// some comment"
              onMount={handleEditorDidMount}
              value={template}
            />
          </div>
          <Documentation entity={entityType} />
          <AttributesModal isOpen={isOpen} setIsOpen={setIsOpen} />
          <ActionsModal isOpen={isActionsModalOpen} setIsOpen={setIsActionsModalOpen} />
        </Split>
        <div className="fixed bg-[var(--bg-primary-warm)] bottom-0 left-[64px] p-4 w-full">
          <Button variant="secondary" className="mr-4" onClick={handleSubmitStrategy}>
            Submit Strategy
          </Button>
          <Button variant="primary" onClick={handleValidateStrategy}>
            Validate Strategy
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditStrategy;
